import React, { useState, useEffect } from 'react';
import { 
  ShieldCheckIcon, 
  CreditCardIcon, 
  ClockIcon, 
  CheckCircleIcon,
  InformationCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';

interface Provider {
  id: string;
  name: string;
  description: string;
  setupCost: number;
  monthlyMinimum: number;
  packages: {
    [key: string]: {
      price: number;
      name: string;
    };
  };
  estimatedTime: string;
  features: string[];
  isAvailable: boolean;
  isRecommended: boolean;
}

interface PaymentOption {
  name: string;
  description: string;
  benefits: string[];
  serviceFee?: number;
}

interface VerificationProviderSelectorProps {
  user: any;
  onProviderSelect: (provider: string, packageType: string, paymentMethod: string) => void;
  onCancel: () => void;
}

const VerificationProviderSelector: React.FC<VerificationProviderSelectorProps> = ({
  user,
  onProviderSelect,
  onCancel
}) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<{[key: string]: PaymentOption}>({});
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<string>('standard');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('intermediary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  console.log(`🔍 Rendering verification provider selector for teacher ${user?.email}`);

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      console.log('📋 Fetching background check providers');
      
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/verification/providers', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers);
        setPaymentOptions(data.paymentOptions);
        
        // Set default selections
        const recommended = data.providers.find((p: Provider) => p.isRecommended);
        if (recommended) {
          setSelectedProvider(recommended.id);
        }
        
        console.log(`✅ Loaded ${data.providers.length} providers`);
      } else {
        throw new Error('Failed to fetch providers');
      }
    } catch (error) {
      console.error('❌ Error fetching providers:', error);
      setError('Failed to load background check providers');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSelect = () => {
    if (!selectedProvider || !selectedPackage || !selectedPaymentMethod) {
      setError('Please select a provider, package, and payment method');
      return;
    }

    console.log(`🎯 Provider selected: ${selectedProvider}, package: ${selectedPackage}, payment: ${selectedPaymentMethod}`);
    onProviderSelect(selectedProvider, selectedPackage, selectedPaymentMethod);
  };

  const getSelectedProviderInfo = () => {
    return providers.find(p => p.id === selectedProvider);
  };

  const getSelectedPackageInfo = () => {
    const provider = getSelectedProviderInfo();
    return provider?.packages[selectedPackage];
  };

  const getTotalCost = () => {
    const packageInfo = getSelectedPackageInfo();
    if (!packageInfo) return 0;

    let total = packageInfo.price;
    if (selectedPaymentMethod === 'intermediary' && paymentOptions.intermediary?.serviceFee) {
      total += paymentOptions.intermediary.serviceFee;
    }
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading providers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchProviders}
          className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <ShieldCheckIcon className="mx-auto h-12 w-12 text-blue-500" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          Choose Your Background Check Provider
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Select the provider and package that best fits your needs
        </p>
      </div>

      {/* Provider Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-gray-900">Background Check Provider</h4>
          <button
            onClick={() => setShowComparison(!showComparison)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showComparison ? 'Hide' : 'Show'} Comparison
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                selectedProvider === provider.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${!provider.isAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => provider.isAvailable && setSelectedProvider(provider.id)}
            >
              {provider.isRecommended && (
                <div className="absolute -top-2 -right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <StarIcon className="h-3 w-3 mr-1" />
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h5 className="font-medium text-gray-900">{provider.name}</h5>
                    {!provider.isAvailable && (
                      <span className="ml-2 text-xs text-gray-500">(Coming Soon)</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{provider.description}</p>
                  
                  <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                    <span>⏱️ {provider.estimatedTime}</span>
                    <span>💰 From ${provider.packages.basic?.price || 'N/A'}</span>
                    {provider.setupCost > 0 && <span>🏗️ ${provider.setupCost} setup</span>}
                  </div>

                  {showComparison && (
                    <div className="mt-3 space-y-1">
                      <h6 className="text-xs font-medium text-gray-700">Features:</h6>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {provider.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedProvider === provider.id
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedProvider === provider.id && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Package Selection */}
      {selectedProvider && (
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Background Check Package</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(getSelectedProviderInfo()?.packages || {}).map(([key, packageInfo]) => (
              <div
                key={key}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPackage === key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPackage(key)}
              >
                <div className="text-center">
                  <h5 className="font-medium text-gray-900">{packageInfo.name}</h5>
                  <p className="text-2xl font-bold text-gray-900 mt-2">${packageInfo.price}</p>
                  <p className="text-sm text-gray-500 capitalize">{key} package</p>
                  
                  {key === 'standard' && (
                    <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      Most Popular
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Method Selection */}
      {selectedProvider && selectedPackage && (
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Payment Method</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(paymentOptions).map(([key, option]) => (
              <div
                key={key}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedPaymentMethod === key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPaymentMethod(key)}
              >
                <div className="flex items-start">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{option.name}</h5>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    
                    <ul className="mt-2 space-y-1">
                      {option.benefits.map((benefit, index) => (
                        <li key={index} className="text-xs text-gray-600 flex items-center">
                          <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                          {benefit}
                        </li>
                      ))}
                    </ul>

                    {option.serviceFee && (
                      <p className="text-xs text-gray-500 mt-2">
                        +${option.serviceFee} service fee
                      </p>
                    )}
                  </div>

                  <div className="ml-4">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      selectedPaymentMethod === key
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedPaymentMethod === key && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {selectedProvider && selectedPackage && selectedPaymentMethod && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Order Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Provider:</span>
              <span className="font-medium">{getSelectedProviderInfo()?.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Package:</span>
              <span className="font-medium">{getSelectedPackageInfo()?.name}</span>
            </div>
            <div className="flex justify-between">
              <span>Background Check:</span>
              <span>${getSelectedPackageInfo()?.price}</span>
            </div>
            {selectedPaymentMethod === 'intermediary' && paymentOptions.intermediary?.serviceFee && (
              <div className="flex justify-between">
                <span>Service Fee:</span>
                <span>${paymentOptions.intermediary.serviceFee}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-medium">
              <span>Total:</span>
              <span>${getTotalCost()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleProviderSelect}
          disabled={!selectedProvider || !selectedPackage || !selectedPaymentMethod}
          className={`flex-1 flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
            selectedProvider && selectedPackage && selectedPaymentMethod
              ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          <CreditCardIcon className="h-4 w-4 mr-2" />
          Continue to Payment
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
      </div>

      {/* Information Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Important Information:</p>
            <ul className="mt-1 space-y-1 text-xs">
              <li>• Background checks are valid for 1 year</li>
              <li>• Results typically arrive within the estimated timeframe</li>
              <li>• You'll receive email instructions after payment</li>
              <li>• Refunds available if background check fails</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationProviderSelector; 